/**
 * @fileoverview Monitor de API para diagnóstico de conectividad OAuth2
 * 
 * Implementa un sistema no invasivo de diagnóstico para monitorear en
 * producción todas las peticiones hacia endpoints de autenticación.
 * Cumple con los requisitos de diagnóstico RFC 6749/6750 para OAuth2.
 */

class ApiMonitor {
  constructor() {
    this.enabled = process.env.NODE_ENV === 'production' || process.env.REACT_APP_DEBUG === 'true';
    this.logs = [];
    this.MAX_LOGS = 100;
    this.storageKey = 'nubelytics_api_telemetry';
    
    // Indicadores de problemas conocidos que buscamos
    this.indicators = {
      dnsResolutionFailures: 0,
      unauthorizedResponses: 0,
      tokenFormatErrors: 0,
      duplicateBearer: 0
    };
  }

  /**
   * Inicializa el monitoreo de API
   * Debe llamarse al inicio de la aplicación
   */
  initialize() {
    if (!this.enabled) return;
    
    console.log('🔍 API Monitor: Inicializando telemetría de autenticación');
    this._loadFromStorage();
    this._interceptFetchAPI();
    this._interceptXHR();
    
    // Registramos errores no capturados
    window.addEventListener('error', (event) => {
      if (event.error?.message?.includes('fetch') || 
          event.error?.message?.includes('token') ||
          event.error?.message?.includes('auth')) {
        this._logEvent('uncaught_error', {
          message: event.error.message,
          url: event.filename,
          stack: event.error.stack?.substring(0, 200)
        });
      }
    });
    
    console.log('✅ API Monitor: Telemetría inicializada correctamente');
  }
  
  /**
   * Obtiene un resumen del estado de conectividad hacia la API
   */
  getStatusSummary() {
    return {
      totalRequests: this.logs.length,
      lastRequest: this.logs[this.logs.length - 1],
      indicators: this.indicators,
      apiBaseUrl: this._getActualApiBaseUrl()
    };
  }
  
  /**
   * Obtiene los logs almacenados por el monitor
   * @returns {Array} Logs de actividad de API
   */
  getLogs() {
    return [...this.logs];
  }
  
  /**
   * Activa el modo depuración permanente para uso en producción
   */
  enableDebugMode() {
    try {
      localStorage.setItem('NUBELYTICS_DEBUG', 'true');
      this.enabled = true;
      console.log('🔍 API Monitor: Modo depuración activado permanentemente');
      return true;
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Desactiva el modo depuración permanente
   */
  disableDebugMode() {
    try {
      localStorage.removeItem('NUBELYTICS_DEBUG');
      this.enabled = process.env.NODE_ENV === 'production';
      console.log('🔍 API Monitor: Modo depuración desactivado');
      return true;
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Registra un evento de diagnóstico
   */
  _logEvent(eventType, data) {
    const event = {
      timestamp: new Date().toISOString(),
      event: eventType,
      data: this._sanitizeData(data)
    };
    
    this.logs.push(event);
    
    // Mantener tamaño limitado
    if (this.logs.length > this.MAX_LOGS) {
      this.logs = this.logs.slice(-this.MAX_LOGS);
    }
    
    // Actualizar indicadores
    this._updateIndicators(eventType, data);
    
    // Guardar en localStorage para persistencia
    this._saveToStorage();
    
    // Output al console.log para diagnóstico inmediato
    console.log(`🔍 API Monitor [${eventType}]:`, data);
  }
  
  /**
   * Sanitiza datos para evitar exponer información sensible
   */
  _sanitizeData(data) {
    if (!data) return data;
    
    const cloned = { ...data };
    
    // Sanitizar tokens
    if (cloned.token) {
      cloned.token = cloned.token.substring(0, 10) + '...';
    }
    
    // Sanitizar client_secret
    if (cloned.client_secret) {
      cloned.client_secret = '[REDACTED]';
    }
    
    // Sanitizar Authorization header
    if (cloned.headers?.Authorization) {
      const authParts = cloned.headers.Authorization.split(' ');
      if (authParts.length > 1) {
        cloned.headers.Authorization = `${authParts[0]} ${authParts[1].substring(0, 10)}...`;
      }
    }
    
    return cloned;
  }
  
  /**
   * Actualiza indicadores basados en eventos
   */
  _updateIndicators(eventType, data) {
    if (eventType === 'api_error' && data.status === 401) {
      this.indicators.unauthorizedResponses++;
    }
    
    if (eventType === 'network_error' && data.message?.includes('ERR_NAME_NOT_RESOLVED')) {
      this.indicators.dnsResolutionFailures++;
    }
    
    if (eventType === 'token_error' && data.message?.includes('format')) {
      this.indicators.tokenFormatErrors++;
    }
    
    if (data.headers?.Authorization?.includes('Bearer Bearer')) {
      this.indicators.duplicateBearer++;
    }
  }
  
  /**
   * Intercepta todas las llamadas API fetch para monitoreárlas
   */
  _interceptFetchAPI() {
    const originalFetch = window.fetch;
    const self = this;
    
    window.fetch = async function(url, options) {
      const startTime = Date.now();
      const isAuthEndpoint = typeof url === 'string' && 
        (url.includes('/auth/') || url.includes('/token'));
      
      try {
        // Solo logueamos detalles para endpoints de autenticación
        if (isAuthEndpoint) {
          self._logEvent('api_request', { 
            url, 
            method: options?.method || 'GET',
            headers: options?.headers
          });
        }
        
        const response = await originalFetch.apply(this, arguments);
        const duration = Date.now() - startTime;
        
        // Solo logueamos detalles completos para endpoints de autenticación
        if (isAuthEndpoint) {
          // Clonar respuesta para poder leerla y devolverla
          const clone = response.clone();
          const text = await clone.text();
          let responseData;
          
          try {
            responseData = JSON.parse(text);
          } catch(e) {
            responseData = { 
              textContent: text.substring(0, 150) + '...',
              parseError: e.message
            };
          }
          
          self._logEvent(
            response.ok ? 'api_success' : 'api_error', 
            { 
              url, 
              status: response.status, 
              duration,
              response: responseData
            }
          );
        } else if (!response.ok && response.status === 401) {
          // Para otras peticiones, solo registramos errores 401
          self._logEvent('api_error', { 
            url, 
            status: response.status, 
            duration
          });
        }
        
        return response;
      } catch (error) {
        self._logEvent('network_error', {
          url,
          message: error.message,
          stack: error.stack?.substring(0, 200)
        });
        throw error;
      }
    };
  }
  
  /**
   * Intercepta peticiones XHR para APIs legacy
   */
  _interceptXHR() {
    const open = XMLHttpRequest.prototype.open;
    const send = XMLHttpRequest.prototype.send;
    const self = this;
    
    XMLHttpRequest.prototype.open = function(method, url) {
      this._url = url;
      this._method = method;
      open.apply(this, arguments);
    };
    
    XMLHttpRequest.prototype.send = function(body) {
      const isAuthEndpoint = typeof this._url === 'string' && 
        (this._url.includes('/auth/') || this._url.includes('/token'));
        
      if (isAuthEndpoint) {
        self._logEvent('xhr_request', {
          url: this._url,
          method: this._method,
          body: body ? body.substring(0, 150) : undefined
        });
        
        this.addEventListener('load', function() {
          if (this.status === 401) {
            self._logEvent('xhr_error', {
              url: this._url,
              status: this.status
            });
          }
        });
        
        this.addEventListener('error', function() {
          self._logEvent('xhr_network_error', {
            url: this._url
          });
        });
      }
      
      send.apply(this, arguments);
    };
  }
  
  /**
   * Persiste logs en localStorage para diagnóstico
   */
  _saveToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify({
        timestamp: new Date().toISOString(),
        logs: this.logs.slice(-30), // guardamos solo los últimos 30 para no saturar
        indicators: this.indicators
      }));
    } catch(e) {
      // Modo privado o localStorage no disponible
      console.warn('No se pudo guardar telemetría en localStorage');
    }
  }
  
  /**
   * Carga logs previos de localStorage
   */
  _loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const data = JSON.parse(stored);
        // Solo restauramos indicadores, no logs
        if (data.indicators) {
          this.indicators = data.indicators;
        }
      }
    } catch(e) {
      // Error al parsear o localStorage no disponible
    }
  }
  
  /**
   * Detecta la URL base de API que se está usando realmente
   */
  _getActualApiBaseUrl() {
    try {
      // Intentar obtener de AppConfig
      const appConfig = require('../infrastructure/config/AppConfig').default;
      return appConfig.apiBaseUrl;
    } catch(e) {
      // Falback a variables de entorno
      return process.env.REACT_APP_API_BASE_URL || 'https://api.nubelytics.com';
    }
  }
}

// Instancia singleton
const apiMonitor = new ApiMonitor();

export default apiMonitor;
